/**
 * Build a standalone Kobe-styled campaign report HTML document from the IMS
 * report payload (passcode endpoint, same as /reports/campaign/summary).
 * Rendered inside an isolated <iframe srcdoc> so the report's dark editorial
 * styling never leaks into the Vuetify SPA (and vice-versa).
 *
 * Posts are flattened by ./reportData (a port of the legacy report's
 * calculatePostData) so numbers match exactly. Per-platform SUMMARY TABLES
 * honour the campaign's own column config (campaign_details.<type>AnalysisStatus:
 * ordered { key: boolean }) for column order + visibility, with hover tooltips.
 * Rows follow the saved arrangement (lineUpIndex). Content is shown as a
 * filterable carousel; clicking a card reveals that content's stats.
 */
import KOBE_STYLE from './style';
import normalizePosts from './reportData';

// tier scale — mirror of ims_campaign_service helper.getInfTier + SGTier/otherTier
const TIERS = [
  { label: 'Tier 1 (250k and up)', from: 250000, to: null },
  { label: 'Tier 2 (100k to 250k)', from: 100000, to: 250000 },
  { label: 'Tier 3 (50k to 100k)', from: 50000, to: 100000 },
  { label: 'Tier 4 (25k to 50k)', from: 25000, to: 50000 },
  { label: 'Tier 5 (10k to 25k)', from: 10000, to: 25000 },
  { label: 'Tier 6 (10k and below)', from: null, to: 10000 }
];
const tierOf = (v) => {
  const num = numeric(v);
  return (TIERS.find((t) => {
    if (t.from !== null && t.to !== null) return num > t.from && num <= t.to;
    if (t.from !== null) return num > t.from;
    if (t.to !== null) return num <= t.to;
    return false;
  }) || {}).label || '—';
};

function numeric(v) {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const x = Number(String(v).replace(/[,%s]/g, '').trim());
  return Number.isFinite(x) ? x : 0;
}
const esc = (s) =>
  String(s === null || s === undefined ? '' : s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
  );
const has = (v) => v !== null && v !== undefined && v !== '';
const compact = (n) => {
  const v = numeric(n);
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(v % 1e6 === 0 ? 0 : 1)}m`;
  if (Math.abs(v) >= 1e3) return `${Math.round(v / 1e3)}k`;
  return String(Math.round(v));
};
const ratio = (a, b) => (numeric(b) > 0 ? (numeric(a) / numeric(b)) * 100 : null);
const numCell = (v) => (has(v) && numeric(v) !== 0 ? compact(v) : has(v) ? '0' : '—');
const pctCell = (v) => (has(v) ? `${numeric(v).toFixed(2)}%` : '—');

const handleOf = (p) => p.name || p.instagram || p.influencer || 'unknown';
const cleanHandle = (h) => String(h || '').trim().replace(/^@/, '');
const profileUrl = (p) => {
  const savedUrl = String(p.profileUrl || '').trim();
  if (/^https?:\/\//i.test(savedUrl)) return savedUrl;

  const handle = cleanHandle(p.profileHandle);
  if (!handle || handle === 'unknown') return null;
  const encodedHandle = encodeURIComponent(handle);
  switch (String(p.profilePlatform || '').toLowerCase()) {
    case 'tiktok': return `https://tiktok.com/@${encodedHandle}`;
    case 'youtube': return `https://youtube.com/${encodedHandle}`;
    case 'facebook': return `https://facebook.com/${encodedHandle}`;
    case 'linkedin': return `https://www.linkedin.com/in/${encodedHandle}`;
    case 'twitter': return `https://x.com/${encodedHandle}`;
    case 'xhs':
    case 'xiaohongshu': return `https://xiaohongshu.com/user/profile/${encodedHandle}`;
    case 'lemon':
    case 'lemon8': return `https://lemon8-app.com/${encodedHandle}`;
    case 'instagram': return `https://instagram.com/${encodedHandle}`;
    default: return null;
  }
};
const creatorLink = (p) => {
  const handle = handleOf(p);
  const url = profileUrl(p);
  const label = `@${esc(handle)}`;
  return url ? `<a class="handle" href="${esc(url)}" target="_blank" rel="noopener">${label}</a>` : `<span class="handle">${label}</span>`;
};
const linkCell = (p) => (p.url ? `<a class="handle" href="${esc(p.url)}" target="_blank" rel="noopener">View ↗</a>` : '—');

// content-type -> config object / enable flag (keyed by the flat post.type)
const TYPE_META = {
  'post/carousel': { status: 'postAnalysisStatus', enable: 'enablePostAnalysis' },
  reel: { status: 'reelAnalysisStatus', enable: 'enableReelAnalysis' },
  story: { status: 'storyAnalysisStatus', enable: null },
  gdrive: { status: 'storyAnalysisStatus', enable: null },
  tiktok: { status: 'tikTokAnalysisStatus', enable: 'enableTikTokAnalysis' },
  youtube: { status: 'youtubeAnalysisStatus', enable: 'enableYoutubeAnalysis' },
  facebook: { status: 'fbAnalysisStatus', enable: 'enableFbAnalysis' },
  linkedin: { status: 'linkedInAnalysisStatus', enable: 'enableLinkedInAnalysis' },
  twitter: { status: 'twitterAnalysisStatus', enable: 'enableTwitterAnalysis' },
  xhs: { status: 'xhsAnalysisStatus', enable: 'enableXHSAnalysis' },
  lemon: { status: 'lemonAnalysisStatus', enable: 'enableLemonAnalysis' }
};

// Master column dictionary: statusKey -> { label, field/get, tooltip, num }.
// Fields map to the flattened (snake_case) post produced by reportData.js;
// tooltips mirror the legendText in the app's *Summary.vue components.
// real info icon (SVG) — a text "i" gets uppercased by the header styles
const INFO_ICON =
  '<svg class="th-info" viewBox="0 0 16 16" width="11" height="11" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.4"/><rect x="7.25" y="6.6" width="1.5" height="4.6" rx="0.5" fill="currentColor"/><circle cx="8" cy="4.4" r="1" fill="currentColor"/></svg>';

const N = (field) => ({ get: (p) => numCell(p[field]), num: true });
const P = (field) => ({ get: (p) => pctCell(p[field]), num: true });
const COLUMN_DEFS = {
  liveDate: { label: 'Live Date', get: (p) => esc(p.live_date || '—'), tip: 'Date the content went live' },
  url: { label: 'Post URL', get: linkCell, tip: 'Link to the published content' },
  outreach: { label: 'Audience Size', tip: 'The number of followers the creator has at the time the post was made', ...N('outreach') },
  showReach: { label: 'Accounts Reached', tip: 'The total number of unique users who viewed the content', ...N('reach') },
  showImpressions: { label: 'Impressions', tip: "The total number of times the content is displayed on a user's feed", ...N('impressions') },
  likes: { label: 'Likes', tip: 'The number of times users have tapped the “like” button on a post', ...N('likes') },
  showStoryLikes: { label: 'Story Likes', tip: 'Likes on the story', ...N('story_likes') },
  comments: { label: 'Comments', tip: 'The number of written responses to a post', ...N('comments') },
  shares: { label: 'Shares', tip: 'The number of times users have reposted / shared a post', ...N('shares') },
  showShares: { label: 'Shares', tip: 'The number of times users have reposted / shared a post', ...N('shares') },
  showBookmarks: { label: 'Bookmarks', tip: 'The number of times users have saved a post to their “Saved” folder', ...N('saved') },
  showReplies: { label: 'Replies', tip: 'Replies to the story', ...N('replies') },
  showLinkClicks: { label: 'Link Clicks', tip: 'Number of taps on links in the story', ...N('link_clicks') },
  showStickerTaps: { label: 'Sticker Taps', tip: 'Number of taps on stickers in the story', ...N('sticker_taps') },
  showInteractions: { label: 'Interactions', tip: 'The sum of likes, comments, saves and shares', ...N('interactions') },
  showViews: { label: 'Plays', tip: 'The number of times the video was played', ...N('views') },
  showPlays: { label: 'Play Time', tip: 'Total play time', ...N('plays') },
  showWatches: { label: 'Avg Watch Time', tip: 'Average watch time per view', get: (p) => (has(p.watches) ? esc(p.watches) : '—'), num: true },
  showEngagement: { label: 'ER by Audience', tip: 'Engagement ÷ audience size', ...P('new_engagement_rate') },
  showNewEngagementRate: { label: 'ER by Audience', tip: 'Engagement ÷ audience size', ...P('new_engagement_rate') },
  showEngagementView: { label: 'Play Rate', tip: 'Plays ÷ audience size', ...P('new_engagement_rate_view') },
  showInteractionPlays: { label: 'ER by Plays', tip: 'Engagement ÷ plays', ...P('interaction_rate_plays') },
  showEngRateReach: { label: 'ER by Reach', tip: 'Engagement ÷ accounts reached', ...P('engagement_rate_reach') },
  showEngRateTotalViewers: { label: 'ER by Reach', tip: 'Engagement ÷ accounts reached', ...P('eng_rate_total_viewers') },
  showAvgEngagement: { label: 'Avg ER', tip: 'The creator’s average engagement rate', ...P('engagement_rate') },
  showMediaEngagement: { label: 'Median ER', tip: 'The creator’s median engagement rate', ...P('median_engagement_rate') },
  showAvgLikes: { label: 'Avg Likes', tip: 'The creator’s average likes', ...N('avg_likes') },
  avgLikes: { label: 'Avg Likes', tip: 'The creator’s average likes', ...N('avg_likes') },
  showAvgComments: { label: 'Avg Comments', tip: 'The creator’s average comments', ...N('avg_comments') },
  avgComments: { label: 'Avg Comments', tip: 'The creator’s average comments', ...N('avg_comments') },
  showAvgViews: { label: 'Avg Views', tip: 'The creator’s average views', ...N('avg_views') },
  avgViews: { label: 'Avg Views', tip: 'The creator’s average views', ...N('avg_views') },
  showAvgReelsPlays: { label: 'Avg Reels Plays', tip: 'The creator’s average reels plays', ...N('avg_reels_plays') },
  avgShares: { label: 'Avg Shares', tip: 'The creator’s average shares', ...N('avg_shares') },
  avgSaves: { label: 'Avg Saves', tip: 'The creator’s average saves', ...N('avg_saves') }
};
const DEFAULT_COLS = {
  'post/carousel': ['liveDate', 'url', 'outreach', 'showReach', 'likes', 'comments', 'showShares', 'showBookmarks', 'showEngagement'],
  reel: ['liveDate', 'url', 'outreach', 'showViews', 'likes', 'comments', 'showShares', 'showBookmarks', 'showInteractionPlays'],
  story: ['liveDate', 'outreach', 'showReach', 'showImpressions', 'showLinkClicks', 'showReplies', 'showShares', 'showInteractions'],
  gdrive: ['liveDate', 'outreach', 'showLinkClicks', 'showReplies', 'showShares', 'showInteractions'],
  tiktok: ['liveDate', 'url', 'outreach', 'showPlays', 'likes', 'comments', 'shares', 'showBookmarks', 'showInteractionPlays'],
  youtube: ['liveDate', 'url', 'outreach', 'showViews', 'likes', 'comments', 'showEngagement'],
  facebook: ['liveDate', 'url', 'outreach', 'likes', 'comments', 'showShares'],
  linkedin: ['liveDate', 'url', 'outreach', 'showImpressions', 'likes', 'comments', 'showShares', 'showBookmarks'],
  twitter: ['liveDate', 'url', 'outreach', 'likes', 'comments', 'showShares', 'showBookmarks', 'showViews'],
  xhs: ['liveDate', 'url', 'outreach', 'showViews', 'likes', 'comments', 'showShares'],
  lemon: ['liveDate', 'url', 'outreach', 'showViews', 'likes', 'comments', 'showShares']
};
function columnKeysFor(type, campaign) {
  const meta = TYPE_META[type] || TYPE_META['post/carousel'];
  const status = campaign[meta.status];
  if (status && typeof status === 'object') {
    const keys = Object.keys(status).filter((k) => status[k] && COLUMN_DEFS[k]);
    if (keys.length) return keys;
  }
  return DEFAULT_COLS[type] || DEFAULT_COLS['post/carousel'];
}
const typeEnabled = (type, campaign) => {
  const enable = (TYPE_META[type] || {}).enable;
  if (!enable) return true;
  const settings = campaign.settings || {};
  return settings[enable] !== false;
};

// ---- KPI row: the Overall section's ACHIEVED numbers ----
// Mirror of the legacy report's grand totals (index.vue): engagement and
// impressions accumulate differently per format; outreachImpressions =
// outreach + impressions (views count as impressions for reels/TikTok).
function computeAchieved(posts) {
  let outreach = 0;
  let engagement = 0;
  let impressions = 0;
  for (const p of posts) {
    outreach += numeric(p.outreach);
    const t = p.type;
    if (t === 'story' || t === 'gdrive') {
      engagement += numeric(p.shares) + numeric(p.replies) + numeric(p.link_clicks) + numeric(p.sticker_taps);
      impressions += numeric(p.impressions);
    } else if (t === 'youtube') {
      engagement += numeric(p.likes) + numeric(p.comments);
      impressions += numeric(p.impressions);
    } else if (t === 'reel' || t === 'tiktok') {
      engagement += numeric(p.likes) + numeric(p.comments) + numeric(p.saved) + numeric(p.shares);
      impressions += numeric(p.impressions) + numeric(p.views);
    } else if (t === 'xhs' || t === 'lemon' || t === 'facebook' || t === 'others') {
      engagement += numeric(p.like_count) + numeric(p.comment_count) + numeric(p.bookmarks) + numeric(p.shares);
      impressions += numeric(p.impressions);
    } else {
      engagement += numeric(p.likes) + numeric(p.comments) + numeric(p.saved) + numeric(p.shares);
      impressions += numeric(p.impressions);
    }
  }
  return { outreach, engagement, outreachImpressions: outreach + impressions };
}

function kpiRow(posts, campaign, totalContents, formats) {
  const achieved = computeAchieved(posts);
  const status = campaign.performanceAnalysisStatus || {};
  const show = (key, def = true) => (status[key] === undefined ? def : !!status[key]);
  const kpis = [];
  if (show('contents')) kpis.push({ label: 'Contents', value: String(totalContents), sub: `${formats} formats` });
  if (show('outreachImpressions')) kpis.push({ label: 'Audience Size + Impressions', value: compact(achieved.outreachImpressions), sub: 'achieved' });
  if (show('outreach')) kpis.push({ label: 'Audience Size', value: compact(achieved.outreach), sub: 'achieved' });
  if (show('interactions')) kpis.push({ label: 'Interactions', value: compact(achieved.engagement), sub: 'achieved' });
  if (!kpis.length) return '';
  return `<div class="kpi-grid" style="grid-template-columns:repeat(${kpis.length},1fr);">${kpis
    .map((k) => `<div class="kpi"><div class="kpi-label">${esc(k.label)}</div><div class="kpi-value">${k.value}</div><div class="kpi-sub">${esc(k.sub)}</div></div>`)
    .join('')}</div>`;
}

// maps the flat report content type onto the canonical section key used by the
// per-table AI analysis editor (campaign.AITableSummaries)
const AI_TABLE_KEY = {
  'post/carousel': 'post',
  reel: 'reel',
  story: 'story',
  gdrive: 'story',
  tiktok: 'tiktok',
  youtube: 'youtube',
  facebook: 'facebook',
  twitter: 'twitter',
  xhs: 'xhs',
  lemon: 'lemon',
  others: 'others'
};

// ---- per-content-type SUMMARY TABLES (config-driven, with tooltips) ----
function summaryTables(groups, campaign) {
  let i = 0;
  return [...groups.entries()]
    .filter(([type]) => typeEnabled(type, campaign))
    .map(([type, entry]) => {
      const cols = columnKeysFor(type, campaign).map((k) => COLUMN_DEFS[k]);
      const head =
        `<th><span class="th-main">Creator</span><span class="th-sub">Handle · Tier</span></th>` +
        cols
          .map((c) => `<th class="${c.num ? 'num-th' : ''}" title="${esc(c.tip || c.label)}">${esc(c.label)}${INFO_ICON}</th>`)
          .join('');
      const body = entry.posts
        .map((p) => {
          const creator = `<td>${creatorLink(p)}<div class="th-sub">${esc(
            tierOf(p.outreach)
          )}</div></td>`;
          return `<tr>${creator}${cols.map((c) => `<td class="${c.num ? 'num' : ''}">${c.get(p)}</td>`).join('')}</tr>`;
        })
        .join('');
      i += 1;
      // Per-table AI paragraph (editable in Campaign Report Internal). The
      // editor keys by canonical section type (post/story/…); map the flat
      // report type onto it (post/carousel->post, gdrive->story).
      const tableAi = (campaign.AITableSummaries || {})[AI_TABLE_KEY[type] || type];
      return `
  <section>
    <div class="section-label">${String(i).padStart(2, '0')} · ${esc(entry.label)} Summary</div>
    <h2>${esc(entry.label)} <span class="accent">performance.</span></h2>
    ${tableAi ? `<p class="table-ai">${esc(tableAi)}</p>` : ''}
    <div class="table-wrap">
      <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    </div>
  </section>`;
    })
    .join('');
}

// ---- creator aggregation (one row per creator, ranked by ER on reach) ----
function aggregateCreators(posts) {
  const by = new Map();
  for (const p of posts) {
    const key = p.influencerID || handleOf(p);
    if (!by.has(key)) by.set(key, { handle: handleOf(p), contents: 0, outreach: 0, plays: 0, interactions: 0, reach: 0, saved: 0, shares: 0 });
    const c = by.get(key);
    c.contents += 1;
    c.outreach = Math.max(c.outreach, numeric(p.outreach));
    c.plays += numeric(p.views);
    c.interactions += numeric(p.interactions);
    c.reach += numeric(p.reach);
    c.saved += numeric(p.saved);
    c.shares += numeric(p.shares);
  }
  return [...by.values()]
    .map((c) => ({
      ...c,
      erReach: ratio(c.interactions, c.reach) !== null ? ratio(c.interactions, c.reach) : ratio(c.interactions, c.outreach),
      saveRate: c.plays > 0 ? ratio(c.saved, c.plays) : null,
      shareRate: c.plays > 0 ? ratio(c.shares, c.plays) : null
    }))
    .sort((a, b) => (b.erReach || 0) - (a.erReach || 0));
}

// ---- AI analysis (Claude via Bedrock, generated at report time) ----
function aiSection(text) {
  if (!text || !String(text).trim()) return '';
  const lines = String(text).trim().split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  let lead = '';
  let rec = '';
  const bullets = [];
  for (const l of lines) {
    if (/^recommendation\s*:/i.test(l)) rec = l.replace(/^recommendation\s*:\s*/i, '');
    else if (/^[•\-*]\s+/.test(l)) bullets.push(l.replace(/^[•\-*]\s+/, ''));
    else if (!lead) lead = l;
    else bullets.push(l);
  }
  return `
  <section class="ai-analysis">
    <div class="section-label">AI Analysis</div>
    <h2>What the <span class="accent">numbers say.</span></h2>
    ${lead ? `<p class="ai-lead">${esc(lead)}</p>` : ''}
    ${bullets.length ? `<ul class="ai-bullets">${bullets.map((b) => `<li>${esc(b)}</li>`).join('')}</ul>` : ''}
    ${rec ? `<div class="ai-rec"><span>Recommendation</span>${esc(rec)}</div>` : ''}
  </section>`;
}

/**
 * Compact performance context for the AI analysis prompt (KPIs, per-type
 * averages, best/weakest creators). Exported so the Vue component can post it
 * to the ai-summary endpoint before rendering.
 */
export function buildAnalysisContext(data) {
  const campaign = data.campaign_details || data.campaign || {};
  const summary = data.summary || {};
  const posts = orderPosts(normalizePosts(data.posts));
  const g = new Map();
  for (const p of posts) {
    const t = p.title || p.type;
    if (!g.has(t)) g.set(t, { contents: 0, er: [], views: [], likes: [], comments: [], saves: [] });
    const x = g.get(t);
    x.contents += 1;
    if (p.new_engagement_rate) x.er.push(numeric(p.new_engagement_rate));
    x.views.push(numeric(p.views));
    x.likes.push(numeric(p.likes));
    x.comments.push(numeric(p.comments));
    x.saves.push(numeric(p.saved));
  }
  const avg = (a) => (a.length ? Math.round((a.reduce((s, v) => s + v, 0) / a.length) * 100) / 100 : 0);
  const byType = [...g.entries()].map(([type, x]) => ({
    type,
    contents: x.contents,
    avgEngagementRatePct: avg(x.er),
    avgPlays: Math.round(avg(x.views)),
    avgLikes: Math.round(avg(x.likes)),
    avgComments: Math.round(avg(x.comments)),
    avgSaves: Math.round(avg(x.saves))
  }));
  const creators = aggregateCreators(posts).map((c) => ({
    handle: c.handle,
    tier: tierOf(c.outreach),
    contents: c.contents,
    outreach: c.outreach,
    erByReachPct: c.erReach === null ? null : Math.round(c.erReach * 100) / 100,
    saveRatePct: c.saveRate === null ? null : Math.round(c.saveRate * 100) / 100
  }));
  return {
    campaign: campaign.name || 'Campaign',
    client: (data.client || {}).name || null,
    period: campaign.campaignPeriod || campaign.targetLocation || null,
    totalContents: posts.length,
    kpis: {
      totalOutreach: numeric(summary.outreach),
      totalEngagement: numeric(summary.engagement),
      campaignEngagementRatePct: numeric(summary.engagement_rate)
    },
    byType,
    topCreators: creators.slice(0, 5),
    weakestCreators: creators.slice(-3).reverse()
  };
}

/**
 * The flat content types present in a report payload, with display labels —
 * drives the per-table AI analysis editor. Exported for the internal page.
 */
export function reportContentTypes(data) {
  const posts = normalizePosts(data.posts);
  const seen = new Map();
  for (const p of posts) {
    const t = p.type || 'post/carousel';
    if (!seen.has(t)) seen.set(t, { type: t, label: p.title || t, contents: 0 });
    seen.get(t).contents += 1;
  }
  return [...seen.values()];
}

/**
 * Compact context for ONE summary table's AI paragraph: that content type's
 * per-post rows only. Exported for the internal page's per-table editor.
 */
export function buildTableAnalysisContext(data, type) {
  const campaign = data.campaign_details || data.campaign || {};
  const rows = normalizePosts(data.posts)
    .filter((p) => (p.type || 'post/carousel') === type)
    .map((p) =>
      Object.fromEntries(
        Object.entries({
          creator: handleOf(p),
          liveDate: p.live_date,
          audienceSize: numeric(p.outreach),
          reach: numeric(p.reach) || undefined,
          impressions: numeric(p.impressions) || undefined,
          plays: numeric(p.views) || undefined,
          likes: p.likes !== undefined ? numeric(p.likes) : undefined,
          comments: p.comments !== undefined ? numeric(p.comments) : undefined,
          shares: numeric(p.shares) || undefined,
          saves: numeric(p.saved) || undefined,
          replies: numeric(p.replies) || undefined,
          linkClicks: numeric(p.link_clicks) || undefined,
          engagementRatePct: p.new_engagement_rate !== undefined ? numeric(p.new_engagement_rate) : undefined
        }).filter(([, v]) => v !== undefined)
      )
    );
  return {
    campaign: campaign.name || 'Campaign',
    contentType: type,
    contents: rows.length,
    rows: rows.slice(0, 60)
  };
}

// ---- content carousel (filterable; click a card to reveal its stats) ----
function embedMarkup(p) {
  const url = p.url || '';
  const t = p.type;
  if (t === 'twitter' || /(?:x\.com|twitter\.com)\/.+\/status\//.test(url)) {
    const postId = (url.match(/\/status(?:es)?\/(\d+)/) || [])[1] || '';
    if (postId) return `<iframe class="social-embed twitter-embed" loading="lazy" src="https://platform.twitter.com/embed/Tweet.html?id=${postId}&theme=dark" title="X (Twitter) post" frameborder="0" scrolling="no"></iframe>`;
  }
  if (t === 'facebook' || /(?:facebook\.com|fb\.watch)\//.test(url)) {
    const facebookUrl = encodeURIComponent(url);
    return `<iframe class="social-embed facebook-embed" loading="lazy" src="https://www.facebook.com/plugins/post.php?href=${facebookUrl}&show_text=true&width=500" title="Facebook post" width="500" height="560" scrolling="no" frameborder="0" allowfullscreen="true" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"></iframe>`;
  }
  if (t === 'linkedin' || /linkedin\.com\/(?:feed\/update|posts)\//.test(url)) {
    let decodedUrl = url;
    try {
      decodedUrl = decodeURIComponent(url);
    } catch (error) {
      decodedUrl = url;
    }
    const urnMatch = decodedUrl.match(/urn:li:(activity|share|ugcPost):(\d+)/i);
    const publicPostMatch = decodedUrl.match(/\/posts\/[^?#]*-(activity|share|ugcPost)-(\d+)(?:-[^/?#]+)?\/?(?:[?#]|$)/i);
    const postIdentifier = urnMatch || publicPostMatch;
    if (postIdentifier) {
      const urnType = postIdentifier[1].toLowerCase() === 'ugcpost' ? 'ugcPost' : postIdentifier[1].toLowerCase();
      return `<iframe class="social-embed linkedin-embed" loading="lazy" src="https://www.linkedin.com/embed/feed/update/urn:li:${urnType}:${postIdentifier[2]}" title="LinkedIn post" frameborder="0" allowfullscreen="true"></iframe>`;
    }
    return `<a class="embed-fallback" href="${esc(url)}" target="_blank" rel="noopener"><span>LinkedIn post</span><b>View on LinkedIn ↗</b></a>`;
  }
  if (t === 'tiktok' || /tiktok\.com/.test(url)) {
    const vid = (url.match(/video\/(\d+)/) || [])[1] || '';
    return `<blockquote class="tiktok-embed" cite="${esc(url)}" data-video-id="${vid}" style="max-width:100%;min-width:280px;margin:0;"><section></section></blockquote>`;
  }
  if (t === 'youtube' || /youtube|youtu\.be/.test(url)) {
    const vid = (url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/) || [])[1] || '';
    if (vid) return `<div class="embed-yt"><iframe loading="lazy" src="https://www.youtube.com/embed/${vid}" allowfullscreen></iframe></div>`;
  }
  if ((t === 'post/carousel' || t === 'reel') && /instagram\.com/.test(url)) {
    return `<blockquote class="instagram-media" data-instgrm-permalink="${esc(url)}" data-instgrm-version="14" style="max-width:100%;min-width:260px;margin:0;"></blockquote>`;
  }
  // story (gdrive) / xhs / lemon / others: CC-uploaded S3 media (image or video)
  const link = p.xhsLink || p.lemonLink || p.postLink || '';
  if (url && /^https?:\/\//.test(url)) {
    const media =
      p.media_type === 'video'
        ? `<video class="content-media" src="${esc(url)}" controls playsinline preload="metadata"></video>`
        : `<img class="content-media" src="${esc(url)}" alt="${esc(handleOf(p))}" loading="lazy" />`;
    return link ? `<a href="${esc(link)}" target="_blank" rel="noopener">${media}</a>` : media;
  }
  return `<div class="content-media placeholder">${esc(p.title || 'Content')}</div>`;
}
const STAT_ROWS = [
  ['Audience', (p) => compact(p.outreach)],
  ['Reach', (p) => (has(p.reach) && numeric(p.reach) ? compact(p.reach) : '—')],
  ['Plays', (p) => (has(p.views) && numeric(p.views) ? compact(p.views) : '—')],
  ['Likes', (p) => numCell(p.likes != null ? p.likes : p.story_likes)],
  ['Comments', (p) => numCell(p.comments)],
  ['Shares', (p) => numCell(p.shares)],
  ['Saves', (p) => numCell(p.saved)],
  ['ER', (p) => pctCell(p.new_engagement_rate != null ? p.new_engagement_rate : p.engagement_rate)]
];
function contentCard(p) {
  const stats = STAT_ROWS.map(([label, get]) => `<div class="cs-row"><span>${esc(label)}</span><b>${get(p)}</b></div>`).join('');
  return `
      <div class="content-card" data-type="${esc(p.type)}" data-handle="${esc(String(handleOf(p)).toLowerCase())}">
        <div class="content-embed">${embedMarkup(p)}</div>
        <div class="content-meta">
          ${creatorLink(p)}
          <span class="content-type-tag">${esc(p.title || p.type)}</span>
        </div>
        <button class="stats-toggle" type="button">Show stats ▾</button>
        <div class="content-stats" hidden>${stats}</div>
      </div>`;
}
function contentSection(posts, groups) {
  const cards = posts.filter((p) => p.url).map(contentCard);
  if (!cards.length) return '';
  const chips =
    `<button class="filter-chip active" data-type="all" type="button">All (${posts.filter((p) => p.url).length})</button>` +
    [...groups.entries()]
      .map(([type, entry]) => {
        const count = entry.posts.filter((p) => p.url).length;
        return count ? `<button class="filter-chip" data-type="${esc(type)}" type="button">${esc(entry.label)} (${count})</button>` : '';
      })
      .join('');
  return `
  <section>
    <div class="section-label">Embedded Content</div>
    <h2>The content, <span class="accent">in the feed.</span></h2>
    <p class="section-lede">Filter by format, search a creator handle, and click any post to reveal its stats.</p>
    <div class="filter-bar">${chips}
      <input id="handleSearch" class="handle-search" type="search" placeholder="Search by handle…" autocomplete="off" />
    </div>
    <div class="content-carousel">${cards.join('')}</div>
    <div class="no-content-match" id="noContentMatch" hidden>No content matches that handle.</div>
  </section>`;
}

const EXTRA_CSS = `
  .table-wrap { overflow-x:auto; }
  table td.num, table th.num-th { text-align:right; white-space:nowrap; }
  .th-info { margin-left:5px; color:var(--text-muted); vertical-align:middle; cursor:help; }
  th[title]:hover .th-info { color:var(--green); }
  .content-media[controls] { background:#000; }
  .ai-lead { font-size:20px; color:var(--text); max-width:840px; margin-bottom:24px; line-height:1.5; }
  .ai-bullets { list-style:none; max-width:840px; margin-bottom:24px; }
  .ai-bullets li { position:relative; padding-left:24px; margin-bottom:12px; color:var(--text-dim); line-height:1.55; }
  .ai-bullets li::before { content:'▸'; position:absolute; left:0; color:var(--green); }
  .ai-rec { border-left:2px solid var(--green); padding:14px 20px; background:var(--bg-card); max-width:840px; color:var(--text); line-height:1.5; }
  .ai-rec span { display:block; font-family:'JetBrains Mono',monospace; font-size:10px; text-transform:uppercase; letter-spacing:1.5px; color:var(--green); margin-bottom:6px; }
  .table-ai { max-width:840px; margin-bottom:24px; color:var(--text-dim); line-height:1.6; border-left:2px solid var(--green); padding:10px 16px; background:var(--bg-card); }
  .handle-search { margin-left:auto; min-width:220px; background:var(--bg-card); border:1px solid var(--border); color:var(--text); font-family:'JetBrains Mono',monospace; font-size:12px; padding:8px 14px; outline:none; }
  .handle-search:focus { border-color:var(--green); }
  .handle-search::placeholder { color:var(--text-muted); }
  .no-content-match { padding:32px 0; color:var(--text-muted); font-family:'JetBrains Mono',monospace; font-size:12px; text-transform:uppercase; letter-spacing:1px; }
  .handle { color: var(--green); font-family:'JetBrains Mono',monospace; text-decoration:none; }
  .filter-bar { display:flex; flex-wrap:wrap; gap:10px; margin-bottom:28px; }
  .filter-chip { background:var(--bg-card); border:1px solid var(--border); color:var(--text-dim); font-family:'JetBrains Mono',monospace; font-size:11px; text-transform:uppercase; letter-spacing:1px; padding:8px 16px; cursor:pointer; transition:all .15s; }
  .filter-chip:hover { border-color:var(--green); color:var(--text); }
  .filter-chip.active { background:var(--green); border-color:var(--green); color:#0A0A0A; font-weight:700; }
  .content-carousel { display:flex; gap:20px; overflow-x:auto; padding-bottom:20px; scroll-snap-type:x mandatory; }
  .content-card { flex:0 0 330px; scroll-snap-align:start; background:var(--bg-card); border:1px solid var(--border); display:flex; flex-direction:column; }
  .content-card:hover { border-color:var(--border-strong); }
  .content-embed { min-height:120px; background:var(--bg-elev); display:flex; justify-content:center; align-items:flex-start; overflow:hidden; }
  .content-media { width:100%; height:auto; display:block; max-height:520px; object-fit:cover; }
  .content-media.placeholder { min-height:280px; display:flex; align-items:center; justify-content:center; color:var(--text-muted); font-family:'Rubik',sans-serif; font-weight:700; }
  .embed-yt { position:relative; width:100%; padding-bottom:120%; height:0; }
  .embed-yt iframe { position:absolute; inset:0; width:100%; height:100%; }
  .social-embed { display:block; width:100%; border:0; background:transparent; }
  .twitter-embed { height:550px; background:#000; }
  .facebook-embed { height:560px; background:#fff; }
  .linkedin-embed { min-height:480px; background:#fff; }
  .embed-fallback { width:100%; min-height:220px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; padding:24px; background:#fff; color:#0A66C2; text-decoration:none; font-family:'Rubik',sans-serif; text-align:center; }
  .embed-fallback span { color:#566070; font-size:13px; }
  .embed-fallback b { font-size:14px; }
  .content-meta { display:flex; justify-content:space-between; align-items:center; gap:8px; padding:14px 16px 0; }
  .content-type-tag { font-family:'JetBrains Mono',monospace; font-size:10px; text-transform:uppercase; color:var(--text-muted); }
  .stats-toggle { margin:12px 16px; background:transparent; border:1px solid var(--border-strong); color:var(--green); font-family:'JetBrains Mono',monospace; font-size:11px; text-transform:uppercase; letter-spacing:1px; padding:8px; cursor:pointer; }
  .stats-toggle:hover { background:var(--green); color:#0A0A0A; }
  .content-stats { border-top:1px solid var(--border); padding:4px 16px 16px; }
  .content-stats[hidden] { display:none; }
  .cs-row { display:flex; justify-content:space-between; padding:7px 0; border-bottom:1px solid var(--border); font-size:13px; }
  .cs-row span { color:var(--text-dim); font-family:'JetBrains Mono',monospace; font-size:11px; text-transform:uppercase; }
  .cs-row b { color:var(--text); font-family:'Rubik',sans-serif; }
  .instagram-media, .tiktok-embed { margin:0 !important; }
`;

const CAROUSEL_JS = `
<script>
(function(){
  function ready(fn){ if(document.readyState!=='loading'){fn();}else{document.addEventListener('DOMContentLoaded',fn);} }
  ready(function(){
    var activeType = 'all';
    var searchTerm = '';
    function applyContentFilters(){
      var anyVisible = false;
      document.querySelectorAll('.content-card').forEach(function(card){
        var typeOk = (activeType==='all' || card.getAttribute('data-type')===activeType);
        var handleOk = !searchTerm || (card.getAttribute('data-handle')||'').indexOf(searchTerm) !== -1;
        var show = typeOk && handleOk;
        card.style.display = show ? '' : 'none';
        if (show) anyVisible = true;
      });
      var empty = document.getElementById('noContentMatch');
      if (empty) { if (anyVisible) empty.setAttribute('hidden',''); else empty.removeAttribute('hidden'); }
    }
    document.querySelectorAll('.filter-chip').forEach(function(chip){
      chip.addEventListener('click', function(){
        activeType = chip.getAttribute('data-type');
        document.querySelectorAll('.filter-chip').forEach(function(c){ c.classList.toggle('active', c===chip); });
        applyContentFilters();
      });
    });
    var search = document.getElementById('handleSearch');
    if (search) {
      search.addEventListener('input', function(){
        searchTerm = String(search.value||'').trim().toLowerCase().replace(/^@/,'');
        applyContentFilters();
      });
    }
    document.querySelectorAll('.stats-toggle').forEach(function(btn){
      btn.addEventListener('click', function(){
        var panel = btn.nextElementSibling;
        if(!panel) return;
        var hidden = panel.hasAttribute('hidden');
        if(hidden){ panel.removeAttribute('hidden'); btn.textContent='Hide stats ▴'; }
        else { panel.setAttribute('hidden',''); btn.textContent='Show stats ▾'; }
      });
    });
  });
})();
</script>`;

// posts follow the saved arrangement (lineUpIndex) within their group
const orderPosts = (posts) =>
  posts
    .map((p, idx) => ({ p, idx }))
    .sort((a, b) => {
      const ai = has(a.p.lineUpIndex) ? numeric(a.p.lineUpIndex) : a.idx;
      const bi = has(b.p.lineUpIndex) ? numeric(b.p.lineUpIndex) : b.idx;
      return ai - bi;
    })
    .map((x) => x.p);

export default function buildReportHtml(data, opts = {}) {
  const campaign = data.campaign_details || data.campaign || {};
  const summary = data.summary || {};
  const client = data.client || campaign.client || {};

  const posts = orderPosts(normalizePosts(data.posts));

  // group by content type, preserving order of appearance
  const groups = new Map();
  for (const p of posts) {
    const type = p.type || 'post/carousel';
    if (!groups.has(type)) groups.set(type, { label: p.title || type, posts: [] });
    groups.get(type).posts.push(p);
  }

  const totalContents = posts.length;
  const period = campaign.campaignPeriod || campaign.targetLocation || '';
  const title = `${campaign.name || 'Campaign'} · Kobē Report`;

  return `<!DOCTYPE html>
<html lang="en"><head>
<meta charset="UTF-8" /><meta name="viewport" content="width=device-width, initial-scale=1.0" />
<meta name="robots" content="noindex, nofollow" />
<title>${esc(title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700;800;900&family=Open+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet">
<style>${KOBE_STYLE}${EXTRA_CSS}</style>
</head><body>
<header class="topbar">
  <div class="logo">${esc(client.name || 'Kobē')} · Campaign Report</div>
  <div class="topbar-meta"><span>${esc(period)}</span><span class="badge">Kobē</span></div>
</header>
<div class="container">
  <div class="hero">
    <div class="section-label">Influencer Campaign Report</div>
    <h1>${esc(campaign.name || 'Campaign')}<br><span class="accent">performance review.</span></h1>
    <p class="subtitle">${totalContents} pieces of content across ${groups.size} format${groups.size === 1 ? '' : 's'}. ${compact(
    summary.outreach
  )} total outreach. Summary tables and live content below.</p>
  </div>
  ${kpiRow(posts, campaign, totalContents, groups.size)}
  ${aiSection(opts.aiAnalysis)}
  ${summaryTables(groups, campaign)}
  ${contentSection(posts, groups)}
</div>
<footer><div class="footer-logo">${esc(title)}</div><div class="footer-tag">All rights reserved. Copyright © 2016-2026 Kobe Global Technologies Pte Ltd.</div></footer>
<script async src="//www.instagram.com/embed.js"></script>
<script async src="https://www.tiktok.com/embed.js"></script>
${CAROUSEL_JS}
</body></html>`;
}



// WEBPACK FOOTER //
// ./src/components/reports/kobe/buildReportHtml.js